import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ICustomer, TCustomerCreate, TCustomerUpdatePayload } from '../customers.interface';
import { CustomersService } from '../customers.service';

const CUSTOMER_NAME_ALREADY_EXISTS_CODE = 'customer_name_already_exists' as const;

interface ICreateCustomerDialogState {
  customer: ICustomer | null;
  customerSubmitLoading: boolean;
  error: string | null;
  nameError: string | null;
}

const initialState: ICreateCustomerDialogState = {
  customer: null,
  customerSubmitLoading: false,
  error: null,
  nameError: null,
};

@Injectable()
export class CreateCustomerDialogStore extends ComponentStore<ICreateCustomerDialogState> {
  public readonly customer$ = this.selectSignal((state: ICreateCustomerDialogState) => state.customer);
  public readonly customerSubmitLoading$ = this.selectSignal(
    (state: ICreateCustomerDialogState) => state.customerSubmitLoading,
  );
  public readonly error$ = this.selectSignal((state: ICreateCustomerDialogState) => state.error);

  public readonly nameError$ = this.selectSignal((state: ICreateCustomerDialogState) => state.nameError);

  public readonly clearNameError = (): void => this.patchState({ nameError: null });

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError((error: HttpErrorResponse) => {
        const nameErrorDetail = this.errorFormatterService
          .getErrorDetails(error.error)
          .find((detail) => detail.code === CUSTOMER_NAME_ALREADY_EXISTS_CODE);

        this.patchState({
          customerSubmitLoading: false,
          error: nameErrorDetail ? null : errorMessage,
          nameError: nameErrorDetail
            ? this.errorFormatterService.getErrorMessage(nameErrorDetail, error.error).message
            : null,
        });

        return EMPTY;
      }),
    );

  public readonly createCustomer = this.effect((trigger$: Observable<TCustomerCreate>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ customerSubmitLoading: true, error: null, nameError: null })),
      switchMap((customer: TCustomerCreate) =>
        this.customersService.createCustomer(customer).pipe(
          tap((customerResponse: ICustomer) => {
            this.patchState({
              customer: customerResponse,
              customerSubmitLoading: false,
            });
          }),
          this.handleError('Failed to submit customer'),
        ),
      ),
    );
  });

  public readonly updateCustomer = this.effect(
    (trigger$: Observable<{ id: string; customer: TCustomerUpdatePayload }>) => {
      return trigger$.pipe(
        tap(() => this.patchState({ customerSubmitLoading: true, error: null, nameError: null })),
        switchMap(({ id, customer }) =>
          this.customersService.updateCustomer(id, customer).pipe(
            tap((customerResponse: ICustomer) => {
              this.patchState({
                customer: customerResponse,
                customerSubmitLoading: false,
              });
            }),
            this.handleError('Failed to submit customer'),
          ),
        ),
      );
    },
  );

  constructor(
    private readonly customersService: CustomersService,
    private readonly errorFormatterService: ErrorFormatterService,
  ) {
    super(initialState);
  }
}
